import React, { useEffect, useMemo, useState } from "react";
import {
  createAdminCareerJob,
  deleteAdminCareerApplication,
  deleteAdminCareerJob,
  getAdminCareerApplications,
  getAdminCareerJobs,
  updateAdminCareerApplicationStatus,
  updateAdminCareerJob,
} from "../../../api/careersAdmin";
import { useNotification } from "../../../context/NotificationContext";
import { resolveMediaUrl } from "../../../utils/media";

const APPLICATION_STATUSES = ["Pending", "Reviewed", "Shortlisted", "Rejected", "Hired"];

const EMPTY_JOB_FORM = {
  jobKey: "",
  title: "",
  department: "",
  employmentType: "",
  location: "",
  description: "",
  howToApply: "",
  deadlineUtc: "",
  displayOrder: 1,
  isActive: true,
  requirements: [{ text: "" }],
};

const toDateTimeLocal = (iso) => {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const toIso = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toISOString();
};

const mapJobToForm = (job) => ({
  jobKey: job?.jobKey || "",
  title: job?.title || "",
  department: job?.department || "",
  employmentType: job?.employmentType || "",
  location: job?.location || "",
  description: job?.description || "",
  howToApply: job?.howToApply || "",
  deadlineUtc: toDateTimeLocal(job?.deadlineUtc),
  displayOrder: Number(job?.displayOrder || 1),
  isActive: typeof job?.isActive === "boolean" ? job.isActive : true,
  requirements: Array.isArray(job?.requirements)
    ? job.requirements.map((r) => ({ text: r?.text || "" }))
    : [{ text: "" }],
});

const toJobPayload = (form) => ({
  jobKey: String(form.jobKey || "").trim(),
  title: String(form.title || "").trim(),
  department: String(form.department || "").trim(),
  employmentType: String(form.employmentType || "").trim(),
  location: String(form.location || "").trim(),
  description: String(form.description || "").trim(),
  howToApply: String(form.howToApply || "").trim(),
  deadlineUtc: toIso(form.deadlineUtc),
  displayOrder: Number(form.displayOrder || 1),
  isActive: Boolean(form.isActive),
  requirements: (form.requirements || [])
    .map((x) => ({ text: String(x?.text || "").trim() }))
    .filter((x) => x.text),
});

const CareersManager = () => {
  const { notify } = useNotification();
  const [jobs, setJobs] = useState([]);
  const [jobsLoading, setJobsLoading] = useState(false);
  const [creatingJob, setCreatingJob] = useState(false);
  const [createJobForm, setCreateJobForm] = useState(EMPTY_JOB_FORM);
  const [editingJobId, setEditingJobId] = useState(null);
  const [editJobForm, setEditJobForm] = useState(EMPTY_JOB_FORM);

  const [applications, setApplications] = useState([]);
  const [applicationsLoading, setApplicationsLoading] = useState(false);
  const [applicationStatusFilter, setApplicationStatusFilter] = useState("Pending");
  const [applicationJobFilter, setApplicationJobFilter] = useState("");
  const [selectedApplication, setSelectedApplication] = useState(null);

  const loadJobs = async () => {
    setJobsLoading(true);
    try {
      const data = await getAdminCareerJobs();
      setJobs(Array.isArray(data) ? data : []);
    } catch (error) {
      notify({ type: "error", title: "Failed to load career jobs", message: error?.response?.data?.message || error?.message || "" });
    } finally {
      setJobsLoading(false);
    }
  };

  const loadApplications = async () => {
    setApplicationsLoading(true);
    try {
      const data = await getAdminCareerApplications({
        status: applicationStatusFilter || undefined,
        jobId: applicationJobFilter || undefined,
      });
      setApplications(Array.isArray(data) ? data : []);
    } catch (error) {
      notify({ type: "error", title: "Failed to load applications", message: error?.response?.data?.message || error?.message || "" });
    } finally {
      setApplicationsLoading(false);
    }
  };

  useEffect(() => {
    loadJobs();
  }, []);

  useEffect(() => {
    loadApplications();
  }, [applicationStatusFilter, applicationJobFilter]);

  const sortedJobs = useMemo(
    () => [...jobs].sort((a, b) => Number(a?.displayOrder || 0) - Number(b?.displayOrder || 0)),
    [jobs]
  );

  const sortedApplications = useMemo(
    () => [...applications].sort((a, b) => new Date(b?.submittedAtUtc || 0).getTime() - new Date(a?.submittedAtUtc || 0).getTime()),
    [applications]
  );

  const handleCreateJob = async () => {
    if (!createJobForm.jobKey.trim() || !createJobForm.title.trim()) {
      notify({ type: "error", title: "Job key and title are required" });
      return;
    }
    setCreatingJob(true);
    try {
      await createAdminCareerJob(toJobPayload(createJobForm));
      notify({ type: "success", title: "Career job created" });
      setCreateJobForm(EMPTY_JOB_FORM);
      await loadJobs();
    } catch (error) {
      notify({ type: "error", title: "Create failed", message: error?.response?.data?.message || error?.message || "" });
    } finally {
      setCreatingJob(false);
    }
  };

  const startEditJob = (job) => {
    setEditingJobId(job.id);
    setEditJobForm(mapJobToForm(job));
  };

  const saveEditJob = async () => {
    if (!editingJobId) return;
    if (!editJobForm.jobKey.trim() || !editJobForm.title.trim()) {
      notify({ type: "error", title: "Job key and title are required" });
      return;
    }
    try {
      await updateAdminCareerJob({ id: editingJobId, payload: toJobPayload(editJobForm) });
      notify({ type: "success", title: "Career job updated" });
      setEditingJobId(null);
      setEditJobForm(EMPTY_JOB_FORM);
      await loadJobs();
    } catch (error) {
      notify({ type: "error", title: "Update failed", message: error?.response?.data?.message || error?.message || "" });
    }
  };

  const removeJob = async (job) => {
    if (!window.confirm(`Delete job "${job?.title || job?.jobKey}"?`)) return;
    try {
      await deleteAdminCareerJob({ id: job.id });
      notify({ type: "success", title: "Career job deleted" });
      await loadJobs();
    } catch (error) {
      notify({ type: "error", title: "Delete failed", message: error?.response?.data?.message || error?.message || "" });
    }
  };

  const updateApplicationStatus = async (app, status) => {
    try {
      await updateAdminCareerApplicationStatus({ id: app.id, status });
      notify({ type: "success", title: `Application marked ${status}` });
      await loadApplications();
    } catch (error) {
      notify({ type: "error", title: "Status update failed", message: error?.response?.data?.message || error?.message || "" });
    }
  };

  const removeApplication = async (app) => {
    if (!window.confirm(`Delete application #${app?.id}?`)) return;
    try {
      await deleteAdminCareerApplication({ id: app.id });
      notify({ type: "success", title: "Application deleted" });
      await loadApplications();
    } catch (error) {
      notify({ type: "error", title: "Delete failed", message: error?.response?.data?.message || error?.message || "" });
    }
  };

  const renderJobForm = ({ form, setForm }) => (
    <div className="grid sm:grid-cols-2 gap-3">
      <label className="text-sm text-slate-700">Job Key
        <input type="text" value={form.jobKey} onChange={(e) => setForm((p) => ({ ...p, jobKey: e.target.value }))} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
      </label>
      <label className="text-sm text-slate-700">Title
        <input type="text" value={form.title} onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
      </label>
      <label className="text-sm text-slate-700">Department
        <input type="text" value={form.department} onChange={(e) => setForm((p) => ({ ...p, department: e.target.value }))} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
      </label>
      <label className="text-sm text-slate-700">Employment Type
        <input type="text" value={form.employmentType} onChange={(e) => setForm((p) => ({ ...p, employmentType: e.target.value }))} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
      </label>
      <label className="text-sm text-slate-700">Location
        <input type="text" value={form.location} onChange={(e) => setForm((p) => ({ ...p, location: e.target.value }))} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
      </label>
      <label className="text-sm text-slate-700">Display Order
        <input type="number" value={form.displayOrder} onChange={(e) => setForm((p) => ({ ...p, displayOrder: e.target.value }))} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
      </label>
      <label className="text-sm text-slate-700 sm:col-span-2">Description
        <textarea value={form.description} onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))} className="mt-1 w-full min-h-[90px] rounded-lg border border-slate-300 px-3 py-2 text-sm" />
      </label>
      <label className="text-sm text-slate-700 sm:col-span-2">How To Apply
        <textarea value={form.howToApply} onChange={(e) => setForm((p) => ({ ...p, howToApply: e.target.value }))} className="mt-1 w-full min-h-[70px] rounded-lg border border-slate-300 px-3 py-2 text-sm" />
      </label>
      <label className="text-sm text-slate-700">Deadline
        <input type="datetime-local" value={form.deadlineUtc} onChange={(e) => setForm((p) => ({ ...p, deadlineUtc: e.target.value }))} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
      </label>
      <label className="inline-flex items-center gap-2 text-sm text-slate-700 mt-7">
        <input type="checkbox" checked={Boolean(form.isActive)} onChange={(e) => setForm((p) => ({ ...p, isActive: e.target.checked }))} />
        Active
      </label>

      <div className="sm:col-span-2 rounded-xl border border-slate-200 p-3">
        <p className="text-sm font-semibold text-[#011337] mb-2">Requirements</p>
        {form.requirements.map((row, idx) => (
          <div key={idx} className="flex gap-2 mb-2">
            <input type="text" value={row?.text || ""} onChange={(e) => setForm((p) => {
              const next = [...p.requirements];
              next[idx] = { text: e.target.value };
              return { ...p, requirements: next };
            })} className="flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm" />
            <button type="button" onClick={() => setForm((p) => {
              const next = p.requirements.filter((_, i) => i !== idx);
              return { ...p, requirements: next.length ? next : [{ text: "" }] };
            })} className="px-3 rounded border border-rose-200 text-rose-700">-</button>
          </div>
        ))}
        <button type="button" onClick={() => setForm((p) => ({ ...p, requirements: [...p.requirements, { text: "" }] }))} className="text-xs px-3 py-1.5 rounded border border-slate-300">Add requirement</button>
      </div>
    </div>
  );

  return (
    <section className="space-y-5">
      <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5">
        <div className="flex items-center justify-between gap-3 mb-4">
          <div>
            <h2 className="text-xl sm:text-2xl font-bold text-[#011337]">Career Jobs Manager</h2>
            <p className="text-sm text-slate-500 mt-1">Create, update and delete career jobs.</p>
          </div>
          <button type="button" onClick={loadJobs} className="px-4 py-2 rounded-lg border border-slate-300 hover:bg-slate-50 text-sm">Reload</button>
        </div>
        {renderJobForm({ form: createJobForm, setForm: setCreateJobForm })}
        <div className="mt-3 flex justify-end">
          <button type="button" disabled={creatingJob} onClick={handleCreateJob} className="px-4 py-2 rounded-lg bg-[#FE7E3F] text-white hover:bg-[#FE7E3F]/90 text-sm disabled:opacity-60">
            {creatingJob ? "Creating..." : "Create job"}
          </button>
        </div>
      </div>

      <div className="overflow-x-auto rounded-xl border border-slate-200 shadow-sm bg-white">
        <table className="min-w-full table-fixed">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">ID</th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Job</th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Department</th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Location</th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Status</th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody>
            {jobsLoading ? (
              <tr><td colSpan={6} className="px-3 py-4 text-sm text-slate-500">Loading jobs...</td></tr>
            ) : sortedJobs.length ? (
              sortedJobs.map((job) => (
                <tr key={job.id} className="border-b border-slate-200 align-top">
                  <td className="px-3 py-3 text-sm text-slate-700">{job.id}</td>
                  <td className="px-3 py-3 text-sm text-slate-900">
                    <p className="font-medium">{job.title || "-"}</p>
                    <p className="text-xs text-slate-500 mt-1">{job.jobKey || "-"}</p>
                  </td>
                  <td className="px-3 py-3 text-sm text-slate-700">{job.department || "-"}</td>
                  <td className="px-3 py-3 text-sm text-slate-700">{job.location || "-"}</td>
                  <td className="px-3 py-3 text-sm">
                    <span className={`px-2 py-1 rounded text-xs ${job.isActive ? "bg-emerald-100 text-emerald-800" : "bg-slate-200 text-slate-700"}`}>
                      {job.isActive ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="px-3 py-3">
                    <div className="flex flex-col gap-2">
                      <button type="button" onClick={() => startEditJob(job)} className="text-xs px-2 py-1.5 rounded border border-[#011337] text-[#011337] hover:bg-[#011337]/10">Edit</button>
                      <button type="button" onClick={() => removeJob(job)} className="text-xs px-2 py-1.5 rounded border border-rose-200 text-rose-700 hover:bg-rose-50">Delete</button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr><td colSpan={6} className="px-3 py-4 text-sm text-slate-500">No jobs found.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {editingJobId ? (
        <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5">
          <div className="flex items-center justify-between gap-3 mb-3">
            <h3 className="text-lg font-semibold text-[#011337]">Edit Career Job #{editingJobId}</h3>
            <button type="button" onClick={() => { setEditingJobId(null); setEditJobForm(EMPTY_JOB_FORM); }} className="text-sm px-3 py-1.5 rounded-lg border border-slate-300 hover:bg-slate-50">Close</button>
          </div>
          {renderJobForm({ form: editJobForm, setForm: setEditJobForm })}
          <div className="mt-3 flex justify-end">
            <button type="button" onClick={saveEditJob} className="px-4 py-2 rounded-lg bg-[#011337] text-white hover:bg-[#011337]/90 text-sm">Save changes</button>
          </div>
        </div>
      ) : null}

      <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5">
        <div className="flex items-center justify-between gap-3 mb-4">
          <div>
            <h2 className="text-xl sm:text-2xl font-bold text-[#011337]">Career Applications</h2>
            <p className="text-sm text-slate-500 mt-1">Review applicants, update status, and delete submissions.</p>
          </div>
          <div className="flex items-center gap-2">
            <select value={applicationStatusFilter} onChange={(e) => setApplicationStatusFilter(e.target.value)} className="rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white">
              {APPLICATION_STATUSES.map((status) => <option key={status} value={status}>{status}</option>)}
            </select>
            <select value={applicationJobFilter} onChange={(e) => setApplicationJobFilter(e.target.value)} className="rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white">
              <option value="">All jobs</option>
              {sortedJobs.map((job) => <option key={job.id} value={job.id}>{job.title}</option>)}
            </select>
            <button type="button" onClick={loadApplications} className="px-4 py-2 rounded-lg border border-slate-300 hover:bg-slate-50 text-sm">Reload</button>
          </div>
        </div>
      </div>

      <div className="overflow-x-auto rounded-xl border border-slate-200 shadow-sm bg-white">
        <table className="min-w-full table-fixed">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">ID</th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Applicant</th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Job</th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Status</th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Files</th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody>
            {applicationsLoading ? (
              <tr><td colSpan={6} className="px-3 py-4 text-sm text-slate-500">Loading applications...</td></tr>
            ) : sortedApplications.length ? (
              sortedApplications.map((app) => (
                <tr key={app.id} className="border-b border-slate-200 align-top">
                  <td className="px-3 py-3 text-sm text-slate-700">{app.id}</td>
                  <td className="px-3 py-3 text-sm text-slate-900">
                    <p className="font-medium">{app.fullName || "-"}</p>
                    <p className="text-xs text-slate-500 mt-1 break-all">{app.email || "-"}</p>
                    <p className="text-xs text-slate-500 mt-1">{app.phoneNumber || "-"}</p>
                  </td>
                  <td className="px-3 py-3 text-sm text-slate-700">{app.jobTitle || app.jobKey || "-"}</td>
                  <td className="px-3 py-3 text-sm">
                    <span className={`px-2 py-1 rounded text-xs ${
                      app.status === "Shortlisted" || app.status === "Hired"
                        ? "bg-emerald-100 text-emerald-800"
                        : app.status === "Rejected"
                        ? "bg-rose-100 text-rose-700"
                        : "bg-amber-100 text-amber-800"
                    }`}>
                      {app.status || "Pending"}
                    </span>
                  </td>
                  <td className="px-3 py-3 text-sm text-slate-700">
                    {Array.isArray(app.files) && app.files.length ? (
                      <div className="flex flex-col gap-1">
                        {app.files.map((file) => {
                          const fileUrl = resolveMediaUrl(file?.fileUrl);
                          return fileUrl ? (
                            <a key={file.id} href={fileUrl} target="_blank" rel="noreferrer" className="text-blue-700 underline break-all">
                              {file.originalFileName || file.fileType || "Open file"}
                            </a>
                          ) : null;
                        })}
                      </div>
                    ) : (
                      <span className="text-slate-500">No files</span>
                    )}
                  </td>
                  <td className="px-3 py-3">
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => setSelectedApplication(app)}
                        className="text-xs px-2 py-1.5 rounded border border-[#011337] text-[#011337] hover:bg-[#011337]/10"
                      >
                        View
                      </button>
                      {APPLICATION_STATUSES.map((status) => (
                        <button key={status} type="button" onClick={() => updateApplicationStatus(app, status)} className="text-xs px-2 py-1.5 rounded border border-slate-300 hover:bg-slate-50">
                          {status}
                        </button>
                      ))}
                      <button type="button" onClick={() => removeApplication(app)} className="text-xs px-2 py-1.5 rounded border border-rose-200 text-rose-700 hover:bg-rose-50">Delete</button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr><td colSpan={6} className="px-3 py-4 text-sm text-slate-500">No applications found.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {selectedApplication ? (
        <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5">
          <div className="flex items-center justify-between gap-3 mb-3">
            <h3 className="text-lg font-semibold text-[#011337]">Application Details #{selectedApplication.id}</h3>
            <button
              type="button"
              onClick={() => setSelectedApplication(null)}
              className="text-sm px-3 py-1.5 rounded-lg border border-slate-300 hover:bg-slate-50"
            >
              Close
            </button>
          </div>

          <div className="grid sm:grid-cols-2 gap-3 text-sm">
            <p><strong>Name:</strong> {selectedApplication.fullName || "-"}</p>
            <p><strong>Email:</strong> {selectedApplication.email || "-"}</p>
            <p><strong>Phone:</strong> {selectedApplication.phoneNumber || "-"}</p>
            <p><strong>Job:</strong> {selectedApplication.jobTitle || selectedApplication.jobKey || "-"}</p>
            <p><strong>Status:</strong> {selectedApplication.status || "-"}</p>
            <p><strong>Submitted:</strong> {selectedApplication.submittedAtUtc ? new Date(selectedApplication.submittedAtUtc).toLocaleString() : "-"}</p>
            <p><strong>Reviewed:</strong> {selectedApplication.reviewedAtUtc ? new Date(selectedApplication.reviewedAtUtc).toLocaleString() : "-"}</p>
            <p><strong>Location:</strong> {selectedApplication.location || "-"}</p>
            <p><strong>City:</strong> {selectedApplication.city || "-"}</p>
            <p><strong>Nationality:</strong> {selectedApplication.nationality || "-"}</p>
            <p><strong>Years of experience:</strong> {selectedApplication.yearsOfExperience || "-"}</p>
            <p><strong>Age:</strong> {selectedApplication.age ?? "-"}</p>
            <p><strong>Date of birth:</strong> {selectedApplication.dateOfBirth || "-"}</p>
            <p><strong>Gender:</strong> {selectedApplication.gender || "-"}</p>
            <p><strong>Applied role:</strong> {selectedApplication.appliedRole || "-"}</p>
            <p><strong>Source:</strong> {selectedApplication.source || "-"}</p>
            <p><strong>High school:</strong> {selectedApplication.highSchool || "-"}</p>
            <p><strong>Diploma:</strong> {selectedApplication.diploma || "-"}</p>
            <p><strong>Bachelor degree:</strong> {selectedApplication.bachelorDegree || "-"}</p>
            <p><strong>Doctorate:</strong> {selectedApplication.doctorate || "-"}</p>
            <p><strong>Other education:</strong> {selectedApplication.otherEducation || "-"}</p>
            <p><strong>Languages spoken:</strong> {selectedApplication.languagesSpoken || "-"}</p>
            <p><strong>Software tools:</strong> {selectedApplication.softwareTools || "-"}</p>
            <p><strong>Total experience years:</strong> {selectedApplication.totalExperienceYears ?? "-"}</p>
            <p><strong>Field of experience:</strong> {selectedApplication.fieldOfExperience || "-"}</p>
            <p><strong>Experience in applied position:</strong> {selectedApplication.experienceInAppliedPosition || "-"}</p>
            <p><strong>Currently employed:</strong> {selectedApplication.currentlyEmployed == null ? "-" : String(selectedApplication.currentlyEmployed)}</p>
            <p><strong>Notice period:</strong> {selectedApplication.noticePeriod || "-"}</p>
            <p><strong>Salary expectation:</strong> {selectedApplication.salaryExpectation ?? "-"} {selectedApplication.salaryCurrency || ""}</p>
          </div>

          {Array.isArray(selectedApplication.files) && selectedApplication.files.length ? (
            <div className="mt-4">
              <p className="text-sm font-semibold text-[#011337] mb-2">Files</p>
              <div className="flex flex-col gap-2">
                {selectedApplication.files.map((file) => {
                  const fileUrl = resolveMediaUrl(file?.fileUrl);
                  return fileUrl ? (
                    <a key={file.id} href={fileUrl} target="_blank" rel="noreferrer" className="text-blue-700 underline break-all text-sm">
                      {file.originalFileName || file.fileType || "Open file"}
                    </a>
                  ) : null;
                })}
              </div>
            </div>
          ) : null}
        </div>
      ) : null}
    </section>
  );
};

export default CareersManager;
